import React, { useState } from "react";
import { Typography } from "antd";
import { DownOutlined, UpOutlined, RightOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

const FAQ_CATEGORIES = [
  {
    id: "general",
    navLabel: "General",
    title: "General & Overview",
    items: [
      {
        question: "What is DealVoice and how does it help sales teams?",
        answer:
          "DealVoice is a voice-first sales acceleration and CRM platform designed to eliminate pipeline leakage. It unifies Android telephony logging, deal progression, proposal generation, and GST-compliant invoicing into a single high-trust workspace.",
      },
      {
        question: "Do we need specialized VoIP hardware or PBX phone lines?",
        answer:
          "No specialized hardware, expensive VoIP setups, or desk phones are required. DealVoice works directly with standard Android mobile SIM cards and web browsers, enabling teams to go live in under 5 minutes with zero capital expenditure.",
      },
      {
        question: "Can I import existing leads from Excel or CSV spreadsheets?",
        answer:
          "Yes. DealVoice provides a 1-click bulk CSV and Excel importer. The smart parser automatically maps company names, contacts, phone numbers, industry tags, and pipeline stages with automated deduplication in seconds.",
      },
    ],
  },
  {
    id: "telephony",
    navLabel: "Telephony",
    title: "Native Android Telephony & Sync",
    items: [
      {
        question: "How does DealVoice automatically track calls without manual entry?",
        answer:
          "DealVoice pairs seamlessly with a native Android telephony application running on your sales team's devices. When a business call is placed or received, the application captures call direction, duration, audio recording blobs, and timestamps, instantly syncing telemetry to the web CRM via real-time WebSockets.",
      },
      {
        question: "How do employee presence tracking and break logs work?",
        answer:
          "The web workspace features real-time WebSocket presence indicators and quick break status triggers (e.g., Lunch, Tea, Meeting). Managers gain live visibility into on-duty reps and call availability curves throughout the business day from 9 AM to 7 PM.",
      },
      {
        question: "Can call audio recordings be played back and downloaded?",
        answer:
          "Yes. Sales managers and authorized reps can review call audio recordings directly from the lead timeline with short-lived presigned URLs for total security and compliance.",
      },
    ],
  },
  {
    id: "crm",
    navLabel: "Pipeline & CRM",
    title: "Pipeline & CRM Progression",
    items: [
      {
        question: "What is the 7-day stagnant deal alert system?",
        answer:
          "Deals that linger in any sales pipeline stage for more than 7 days without recorded customer interaction are automatically flagged in the Stagnant Deals Warning Radar. This alert appears on both the Sales Rep daily action dashboard and the Executive console, ensuring hot opportunities never slip through the cracks.",
      },
      {
        question: "How does deal stage progression and handover work?",
        answer:
          "Leads move across customizable pipeline stages with visual drag-and-drop or status updates. Reps can add conversation notes, schedule follow-ups, and trigger handoffs with automated activity ledger logs.",
      },
    ],
  },
  {
    id: "billing",
    navLabel: "Invoicing & GST",
    title: "Invoices, Proposals & GST",
    items: [
      {
        question: "Can I generate GST-compliant invoices and collect payments?",
        answer:
          "Yes. DealVoice includes a built-in Commercial Suite. You can generate itemized quotations with version control (v1, v2), convert approved quotes into GST invoices (with automated CGST, SGST, IGST calculations), and generate instant public payment links powered by Razorpay for UPI, NetBanking, and Cards.",
      },
      {
        question: "Does DealVoice support automated payment verification via Razorpay?",
        answer:
          "Yes. Razorpay webhooks automatically update invoice statuses from Pending to Paid in real time, trigger instant receipt dispatch, and unlock downstream deal stages.",
      },
    ],
  },
  {
    id: "security",
    navLabel: "Security",
    title: "Enterprise Security & Data Isolation",
    items: [
      {
        question: "How is our company data kept secure and separated?",
        answer:
          "DealVoice uses an enterprise multi-tenant database isolation model. Each registered company receives a unique Company Code (e.g. STP-1603) and operates inside its own segregated database container namespace (salesdb_companycode). Documents in the KYC vault are encrypted with AES-256 and protected by TLS 1.3 in transit.",
      },
      {
        question: "Who owns our customer data and call records?",
        answer:
          "You own 100% of your data. DealVoice never sells, shares, or co-mingles your proprietary sales intelligence, customer contacts, or audio files with any other tenant.",
      },
    ],
  },
];

function FaqSection() {
  const [activeCategoryId, setActiveCategoryId] = useState("general");
  const [openIdx, setOpenIdx] = useState(0);

  const currentCategory =
    FAQ_CATEGORIES.find((cat) => cat.id === activeCategoryId) ||
    FAQ_CATEGORIES[0];

  return (
    <section className="faq-section" id="faq">
      <div className="faq-container">
        {/* Header (Left-Aligned) */}
        <div className="faq-header-wrap">
          <span className="section-eyebrow-badge">FREQUENTLY ASKED QUESTIONS</span>
          <Title level={2} className="faq-main-heading">
            Everything you need to know{" "}
            <span className="text-highlight-blue">about DealVoice</span>
          </Title>
          <Paragraph className="faq-main-subheading">
            Got questions about automated telephony sync, pipeline management, or dedicated database isolation? We've got answers.
          </Paragraph>
        </div>

        {/* 2-Column Category Layout (Matching Reference) */}
        <div className="faq-layout-grid">
          {/* Left Column: Category Navigation */}
          <nav className="faq-sidebar-nav" aria-label="FAQ Categories">
            {FAQ_CATEGORIES.map((cat) => {
              const isActive = cat.id === activeCategoryId;
              return (
                <button
                  key={cat.id}
                  type="button"
                  className={`faq-nav-item ${isActive ? "active" : ""}`}
                  onClick={() => {
                    setActiveCategoryId(cat.id);
                    setOpenIdx(0);
                  }}
                >
                  <span className={`faq-nav-arrow ${isActive ? "is-visible" : ""}`}>
                    <RightOutlined style={{ fontSize: "11px" }} />
                  </span>
                  <span className="faq-nav-label">{cat.navLabel}</span>
                </button>
              );
            })}
          </nav>

          {/* Right Column: Category Title & FAQ Accordion List */}
          <div className="faq-content-pane" key={currentCategory.id}>
            <h3 className="faq-category-title">{currentCategory.title}</h3>

            <div className="faq-clean-list">
              {currentCategory.items.map((item, idx) => {
                const isOpen = openIdx === idx;
                return (
                  <div
                    key={idx}
                    className={`faq-clean-item ${isOpen ? "is-open" : ""}`}
                  >
                    <button
                      type="button"
                      className="faq-clean-trigger"
                      onClick={() => setOpenIdx(isOpen ? null : idx)}
                      aria-expanded={isOpen}
                    >
                      <span className="faq-clean-qtext">{item.question}</span>
                      <span className="faq-clean-icon">
                        <DownOutlined />
                      </span>
                    </button>
                    <div className={`faq-clean-collapse ${isOpen ? "is-open" : ""}`}>
                      <div className="faq-clean-collapse-inner">
                        <p className="faq-clean-atext">{item.answer}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FaqSection;
